import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Small, StyledLink, Icon, Flex } from "orcs-design-system";
import getFaviconUrl from "src/util/getFaviconUrl";
import isValidEmail from "src/util/email";
import { normalizeEmailToMailto } from "src/util/urlValidation";

const AppSwitchLink = ({
  label,
  description,
  href,
  iconUrl,
  showFavicon: showFaviconProp = true,
  onClick,
  "data-testid": dataTestId,
}) => {
  const [faviconFailed, setFaviconFailed] = useState(false);

  // Check if href is an email address or mailto: link
  const isEmailLink = useMemo(() => {
    if (typeof href !== "string" || !href) {
      return false;
    }
    return (
      href.toLowerCase().startsWith("mailto:") || isValidEmail(href.trim())
    );
  }, [href]);

  // Normalize email addresses to mailto: format
  const normalizedHref = useMemo(() => normalizeEmailToMailto(href), [href]);

  // Use provided iconUrl, or try to get favicon from href (skip when showFavicon false or email links)
  let displayIconUrl = iconUrl ?? null;
  if (
    showFaviconProp &&
    !displayIconUrl &&
    !isEmailLink &&
    typeof href === "string" &&
    href
  ) {
    displayIconUrl = getFaviconUrl(href);
  }
  const showIcon = showFaviconProp && displayIconUrl && !faviconFailed;

  const handleIconError = () => {
    setFaviconFailed(true);
  };

  return (
    <Flex flexDirection="column" gap="s">
      <StyledLink
        href={normalizedHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        data-testid={dataTestId}
        display="block"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {isEmailLink ? (
          <Icon
            icon={["fas", "envelope"]}
            title="Email icon"
            style={{
              width: "16px",
              height: "16px",
            }}
          />
        ) : showIcon ? (
          <img
            src={displayIconUrl}
            alt=""
            referrerPolicy="no-referrer"
            style={{
              width: "16px",
              height: "16px",
              objectFit: "contain",
            }}
            onError={handleIconError}
          />
        ) : (
          <Icon
            icon={["fas", "external-link-alt"]}
            title="External link icon"
            style={{
              width: "16px",
              height: "16px",
            }}
          />
        )}
        {label}
      </StyledLink>
      {description && (
        <Small display="block" color="greyDark">
          {description}
        </Small>
      )}
    </Flex>
  );
};

AppSwitchLink.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  href: PropTypes.string,
  iconUrl: PropTypes.string,
  showFavicon: PropTypes.bool,
  onClick: PropTypes.func,
  "data-testid": PropTypes.string,
};

export default AppSwitchLink;
